import DownloadButton from '@components/DownloadButton'
import DownloadErrorDialog from '@components/DownloadErrorDialog'
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material'
import { downloadFile, monthNames, years } from '@utils'
import { useState } from 'react'
import DashboardLayout from '../components/DashboardLayout'
import { STAFF_REPORTS_URL } from '@enums/api'

const REPORT_API_ENDPOINT = `${STAFF_REPORTS_URL}/eligible-users`

const EligibleUsersReportPage = () => {
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1)
    const [year, setYear] = useState<number>(new Date().getFullYear())

    const [downloading, setDownloading] = useState<boolean>(false)
    const [downloadError, setDownloadError] = useState<string | null>(null)

    /**
     * @param {number} monthNumber For Zod validation, months are 1-indexed (January = 1, February = 2, etc.)
     */
    const monthName = (monthNumber: number) => {
        return monthNames[monthNumber - 1]
    }

    const handleDownload = async () => {
        setDownloading(true)
        const fileName = `eligible-users_${year}-${monthName(month)}.xlsx`

        // the backend is expecting the "to" to be exclusive, so add a day to
        // fake an inclusive bound
        const query = `?year=${year}&month=${month}`

        try {
            await downloadFile({
                acceptType:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                fileURL: REPORT_API_ENDPOINT + query,
                saveAsFileName: fileName,
            })
        } catch (error) {
            console.error(error)
            setDownloadError('Failed to download report')
        } finally {
            setDownloading(false)
        }
    }
    return (
        <DashboardLayout>
            <DownloadErrorDialog
                onClose={() => setDownloadError(null)}
                open={!!downloadError}
                title={downloadError}
            />
            <Box
                sx={{
                    justifyContent: 'center',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Paper>
                    <Box
                        sx={{
                            padding: 8,
                        }}
                    >
                        <Typography variant="body1" marginBottom={2}>
                            Select month and year to generate the report for.
                        </Typography>
                        <Stack direction="row" sx={{ gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel id="monthSelector">
                                    Month
                                </InputLabel>
                                <Select
                                    labelId="monthSelector"
                                    label="Month"
                                    defaultValue={monthName(month)}
                                    onChange={(e) =>
                                        setMonth(
                                            monthNames.indexOf(e.target.value) +
                                                1
                                        )
                                    }
                                >
                                    {monthNames.map((month, index) => (
                                        <MenuItem key={index} value={month}>
                                            {month}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="yearSelector">Year</InputLabel>
                                <Select
                                    labelId="yearSelector"
                                    label="Year"
                                    defaultValue={year}
                                    onChange={(e) => {
                                        if (typeof e.target.value === 'string')
                                            return
                                        setYear(e.target.value)
                                    }}
                                >
                                    {years.map((year, index) => (
                                        <MenuItem key={index} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        <Box style={{ marginTop: 32 }}>
                            <DownloadButton
                                downloading={downloading}
                                onClick={handleDownload}
                            >
                                Download report
                            </DownloadButton>
                            <Typography
                                variant="body1"
                                style={{ marginTop: 8 }}
                            >
                                <small>
                                    This will download a single-sheet{' '}
                                    <code>.xlsx</code> file
                                </small>
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </DashboardLayout>
    )
}

export default EligibleUsersReportPage
